import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import CopyToClipboardButton from 'components/CopyToClipboardButton';
import ButtonIcon from 'components/ButtonIcon';
import Plus from 'components/icons/plus';
import Pencil from 'components/icons/pencil2';
import ChevronDown from 'components/icons/chevronDown';
import Globe from 'components/icons/globe';
import Users from 'components/icons/users';
import WorkflowIcon from 'components/icons/workflow';
import useHttpQuery from 'data/firestore/useHttpQuery';
import useManageSipDomains from 'data/hooks/useManageSipDomains';
import useManageSipUsers from 'data/hooks/useManageSipUsers';
import { ACTIONS_BASE_URL } from '../../../../constants';
import EditDomainDrawer from '../EditDomainDrawer';
import {
    DomainCardContainer,
    DomainCardHeader,
    DomainIconContainer,
    DomainInfo,
    DomainTitle,
    DomainSubtitleRow,
    DomainSubtitle,
    UserCountText,
    SubtitleSeparator,
    WorkflowNameText,
    NoWorkflowWarning,
    HeaderActions,
    ChevronIcon,
    DomainCardBody,
    EmptyUsersMessage,
} from './styled';
import UserItem from './UserItem';
import CreateSipUserDrawer from './CreateSipUserDrawer';
import EditSipUserDrawer from './EditSipUserDrawer';

interface SipDomain {
    domainSid: string;
    domainName: string;
    friendlyName?: string;
    voiceUrl?: string;
}

interface SipUser {
    id: string;
    username: string;
    sipAddress: string;
    friendlyName?: string;
}

interface Props {
    domain: SipDomain;
}

const DomainCard = ({ domain }: Props) => {
    const { agentId = '' } = useParams();
    const {
        domainSid, domainName, friendlyName, voiceUrl,
    } = domain;
    const [isExpanded, setIsExpanded] = useState(true);

    const { create, edit: editUser, remove } = useManageSipUsers({ agentId, domainSid });
    const { edit } = useManageSipDomains({ agentId });

    const workflowId = useMemo(() => {
        if (!voiceUrl) return undefined;
        return voiceUrl.match(/\/workflows\/([^/]+)\/invoke/)?.[1];
    }, [voiceUrl]);

    const { data: workflowData } = useHttpQuery<{ data: { name: string } }>({
        pathname: `/workflows/${workflowId}`,
        server: ACTIONS_BASE_URL,
        key: ['workflow', workflowId],
        queryOptions: { enabled: Boolean(workflowId) },
    });

    const workflowName = workflowData?.data?.name;

    const { data: users = [], isLoading } = useHttpQuery<SipUser[]>({
        pathname: `/agents/${agentId}/sip-domains/${domainSid}/users`,
        key: ['sip-users', agentId, domainSid],
        queryOptions: { enabled: Boolean(agentId && domainSid) },
    });

    const handleAddUser = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
        create();
    }, [create]);

    const handleEdit = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
        (edit as (domainSid: string, voiceUrl?: string) => void)(domainSid, voiceUrl);
    }, [edit, domainSid, voiceUrl]);

    return (
        <>
            <DomainCardContainer>
                <DomainCardHeader
                    type="button"
                    onClick={() => setIsExpanded((prev) => !prev)}
                >
                    <DomainIconContainer>
                        <Globe />
                    </DomainIconContainer>
                    <DomainInfo>
                        <DomainTitle>{friendlyName || domainName}</DomainTitle>
                        <DomainSubtitleRow>
                            {!workflowId && (
                                <>
                                    <NoWorkflowWarning>No workflow connected</NoWorkflowWarning>
                                    <SubtitleSeparator>·</SubtitleSeparator>
                                </>
                            )}
                            {workflowId && workflowName && (
                                <>
                                    <WorkflowNameText>
                                        <WorkflowIcon />
                                        {workflowName}
                                    </WorkflowNameText>
                                    <SubtitleSeparator>·</SubtitleSeparator>
                                </>
                            )}
                            {!isLoading && (
                                <>
                                    <UserCountText>
                                        <Users />
                                        {users.length}
                                        {' '}
                                        {users.length === 1 ? 'user' : 'users'}
                                    </UserCountText>
                                    <SubtitleSeparator>·</SubtitleSeparator>
                                </>
                            )}
                            <DomainSubtitle>{domainName}</DomainSubtitle>
                            <CopyToClipboardButton
                                value={domainName}
                                mode="icon"
                                size="small"
                                variant="base"
                            />
                        </DomainSubtitleRow>
                    </DomainInfo>
                    <HeaderActions>
                        <ButtonIcon
                            borderRadius="semi-square"
                            icon={<Pencil />}
                            tooltip="Edit domain"
                            size="small"
                            onClick={handleEdit}
                        />
                        <ButtonIcon
                            borderRadius="semi-square"
                            icon={<Plus />}
                            tooltip="Add SIP user"
                            size="small"
                            onClick={handleAddUser}
                        />
                    </HeaderActions>
                    <ChevronIcon $isExpanded={isExpanded}>
                        <ChevronDown />
                    </ChevronIcon>
                </DomainCardHeader>
                {isExpanded && (
                    <DomainCardBody>
                        {!isLoading && users.length === 0 && (
                            <EmptyUsersMessage>
                                No users yet. Click + to add one.
                            </EmptyUsersMessage>
                        )}
                        {users.map((user) => (
                            <UserItem
                                key={user.id}
                                user={user}
                                onEdit={() => editUser(user.id, user.friendlyName)}
                                onDelete={() => remove(user.id)}
                            />
                        ))}
                    </DomainCardBody>
                )}
            </DomainCardContainer>
            <CreateSipUserDrawer />
            <EditSipUserDrawer />
            <EditDomainDrawer />
        </>
    );
};

export default DomainCard;
